// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return, react-hooks/exhaustive-deps, react-hooks/rules-of-hooks */

import React, { FC, useEffect, useState, useMemo } from "react";
import { Button, Form, Modal } from "semantic-ui-react";
import * as excelGenerator from "../../../apis/excelGeneratorApi.js";
import * as protocolOptions from "../../../apis/protocolApi.js";
import { runExcelExport, exportFailureMessage, triggerExcelDownload } from "../../../utilities/excelExportJob";
import { useAuth0 } from "../../../auth/auth0";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useUserAuth } from "../../../hooks/useUserAuth";
import { useModuleNavigation } from "../../../hooks/useModuleNavigation";
import _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";
import type { ProtocolDashboardOptions } from "../../../apis/types/protocolTypes";

enum ExportTypes {
	Universal = "Universal",
	Tabbed = "Tabbed",
	Standard = "Standard",
	PlotGPSCoords = "PlotGPSCoords",
}

type Props = {
	id: string;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	name?: string;
	type: "Trial" | "Field" | "Protocol";
};

const dataDownload: {
	id: string;
	includeSubplots: boolean;
	quantifiedRegion: string;
	isForAdmin: boolean;
	exclusions: boolean;
	isForCSV: boolean;
	isZipped: boolean;
	currentDateTimeString: string;
	/** Download name, extension omitted — the backend appends the one it actually generated. */
	fileName: string;
} = {
	id: "",
	includeSubplots: false,
	quantifiedRegion: "",
	isForAdmin: false,
	exclusions: false,
	isForCSV: false,
	isZipped: false,
	currentDateTimeString: JSON.stringify(new Date().toLocaleString()),
	fileName: "",
};

/** Per-endpoint label used in the download name, e.g. `Corn 24A_UniversalTrialData_20260826_0230pm`. */
const FILE_NAME_LABELS: Record<Props["type"], Record<ExportTypes, string>> = {
	Trial: {
		[ExportTypes.Universal]: "UniversalTrialData",
		[ExportTypes.Standard]: "StandardTrialData",
		[ExportTypes.Tabbed]: "TabbedTrialData",
		[ExportTypes.PlotGPSCoords]: "GPSPlotTrialCoordinateData",
	},
	Field: {
		[ExportTypes.Universal]: "UniversalFieldData",
		[ExportTypes.Standard]: "StandardFieldData",
		[ExportTypes.Tabbed]: "TabbedFieldData",
		[ExportTypes.PlotGPSCoords]: "GPSPlotFieldCoordinateData",
	},
	Protocol: {
		[ExportTypes.Universal]: "UniversalProtocolData",
		[ExportTypes.Standard]: "StandardProtocolData",
		[ExportTypes.Tabbed]: "TabbedProtocolData",
		[ExportTypes.PlotGPSCoords]: "GPSPlotProtocolCoordinateData",
	},
};

type ExportRequest = {
	/** Fires the matching `Generate*` endpoint and resolves to the queued-job envelope. */
	start: (vars: typeof dataDownload) => Promise<any>;
	/** Snapshot of the form selections, taken when the export is dispatched. */
	vars: typeof dataDownload;
	toastId: string;
	/** Preserves the per-export-type reset behaviour the individual mutators used to have. */
	resetAfter: boolean;
};

const HeatmapExportModal: FC<Props> = ({ id, open, setOpen, name, type }) => {
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const entityNames = useSelector((state) => (state.entityNames ? state.entityNames : null));

	//Dropdowns
	const [fileFormatOptions, setFileFormatOptions] = useState([] as any[]);
	const [dataUnitOptions, setDataUnitOptions] = useState([] as any[]);
	const [filteredDataUnitOptions, setFilteredDataUnitOptions] = useState([] as any[]);
	const [quantifiedRegionOptions, setQuantifiedRegionOptions] = useState([] as any[]);
	const [filteredQuantifiedRegionOptions, setFilteredQuantifiedRegionOptions] = useState([] as any[]);
	const [dataContentOptions, setDataContentOptions] = useState([] as any[]);
	const [filteredDataContentOptions, setFilteredDataContentOptions] = useState([] as any[]);

	//Selected Values
	const [fileFormat, setFileFormat] = useState<string | undefined>(undefined);
	const [dataUnit, setDataUnit] = useState<string | undefined>(undefined);
	const [quantifiedRegion, setQuantifiedRegion] = useState<string | undefined>(undefined);
	const [dataContent, setDataContent] = useState<string | undefined>(undefined);

	const [localEntityName, setLocalEntityName] = useState(name);
	const [dashboardOptions, setDashboardOptions] = useState<ProtocolDashboardOptions | null>(null);

	const entityName = useMemo(() => {
		return type === "Trial"
			? entityNames?.trialName
			: type === "Field"
				? entityNames?.fieldName
				: entityNames?.protocolName;
	}, [entityNames, type]);

	const isFieldScaleTrial = useMemo(() => {
		return entityNames?.fieldScaleTrial;
	}, [entityNames]);

	const isApAdmin = userAuth.isApAdmin;

	useEffect(() => {
		setupFileFormatOptions();
		setupDataUnitOptions();
		setupQuantifiedRegionOptions();
		setupDataContentOptions();
	}, [isApAdmin, isFieldScaleTrial]);

	useEffect(() => {
		if (entityName) {
			setLocalEntityName(entityName);
		}
	}, [entityName]);

	//Used to get trial ids for Protocol downloads
	useEffect(() => {
		if (type === "Protocol" && !dashboardOptions) {
			protocolDashboardOptionsMutate();
		}
	}, [type, id]);

	const setupFileFormatOptions = () => {
		// Exactly one option must be default and must never be hidden
		const options = [
			{
				key: "tabbedDataFrame",
				text: "Tabbed Data Frames (.xlsx)",
				value: "tabbedDataFrame",
				isForCSV: false,
				isZipped: false,
				type: ExportTypes.Tabbed,
				exclusions: true,
				hidden: isFieldScaleTrial,
				isForStackedDownload: true,
				default: false,
			},
			{
				key: "universalDataFrame",
				text: "Universal Data Frames (.csv)",
				value: "universalDataFrame",
				isForCSV: true,
				isZipped: false,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
				default: false,
			},
			{
				key: "universalDataFrameExcel",
				text: "Universal Data Frames (.xlsx)",
				value: "universalDataFrameExcel",
				isForCSV: false,
				isZipped: false,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
				default: false,
			},
			{
				key: "universalDataFrameZipped",
				text: "Universal Data Frames (zipped .csv)",
				value: "universalDataFrameZipped",
				isForCSV: true,
				isZipped: true,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: type !== "Protocol",
				isForStackedDownload: false,
				default: false,
			},
			{
				key: "universalDataFrameExcelZipped",
				text: "Universal Data Frames (zipped .xlsx)",
				value: "universalDataFrameExcelZipped",
				isForCSV: false,
				isZipped: true,
				type: ExportTypes.Universal,
				exclusions: true,
				hidden: type !== "Protocol",
				isForStackedDownload: false,
				default: false,
			},
			{
				key: "standardDataFrame",
				text: "Standard (.xlsx)",
				value: "standardDataFrame",
				isForCSV: false,
				isZipped: false,
				type: ExportTypes.Standard,
				exclusions: true,
				hidden: false,
				isForStackedDownload: false,
				default: true,
			},
			{
				key: "standardNoExclusionsDataFrame",
				text: "Standard Without Exclusions (.xlsx)",
				value: "standardNoExclusionsDataFrame",
				isForCSV: false,
				isZipped: false,
				type: ExportTypes.Standard,
				exclusions: false,
				hidden: false,
				isForStackedDownload: false,
				default: false,
			},
			{
				key: "plotGPSCoords",
				text: "Plot GPS Coordinates (.xlsx)",
				value: "plotGPSCoords",
				isForCSV: false,
				isZipped: false,
				type: ExportTypes.PlotGPSCoords,
				exclusions: false,
				hidden: !isApAdmin,
				isForStackedDownload: false,
				default: false,
			},
		];

		setFileFormatOptions(_.filter(options, (o) => !o.hidden));
		setFileFormat(_.find(options, { default: true })?.value);
	};

	const setupDataUnitOptions = () => {
		const options = [
			{ key: "plots", text: "Plots", value: "plots", hidden: false, includeSubplots: false },
			{ key: "subplots", text: "Subplots (Admin Only)", value: "subplots", hidden: !isApAdmin, includeSubplots: true },
		];

		setDataUnitOptions(_.filter(options, (o) => !o.hidden));
		setFilteredDataUnitOptions(_.filter(options, (o) => !o.hidden && o.value !== "subplots"));
		setDataUnit(options[0].value);
	};

	const setupQuantifiedRegionOptions = () => {
		const options = [
			{ key: "standard", text: "Default Qr", value: "standard", hidden: false },
			{ key: "all", text: "All Qrs", value: "all", hidden: false },
		];

		setQuantifiedRegionOptions(_.filter(options, (o) => !o.hidden));
		setFilteredQuantifiedRegionOptions(_.filter(options, (o) => !o.hidden && o.value !== "all"));
		setQuantifiedRegion(options[0].value);
	};

	const setupDataContentOptions = () => {
		const options = [
			{ key: "standard", text: "Standard Data", value: "standard", hidden: false, isForAdmin: false },
			{
				key: "all",
				text: "All Data (Admin Only)",
				value: "all",
				hidden: !isApAdmin || type === "Field",
				isForAdmin: true,
			},
		];

		setDataContentOptions(_.filter(options, (o) => !o.hidden));
		setFilteredDataContentOptions(_.filter(options, (o) => !o.hidden));
		setDataContent(options[0].value);
	};

	const resetInputs = () => {
		setFileFormat(_.find(fileFormatOptions, { default: true })?.value);
		setDataUnit(dataUnitOptions[0].value);
		setQuantifiedRegion(quantifiedRegionOptions[0].value);
		setDataContent(dataContentOptions[0].value);
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterDataUnitOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (match.type == ExportTypes.Standard) {
			setFilteredDataUnitOptions(_.filter(dataUnitOptions, (o) => o.value !== "subplots"));
			setDataUnit(dataUnitOptions[0].value);
		} else {
			setFilteredDataUnitOptions(dataUnitOptions);
		}
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterQuantifiedRegionOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (
			match?.type == ExportTypes.Standard ||
			(match?.type == ExportTypes.PlotGPSCoords && dataUnit == "plots") ||
			(match == undefined && value == "plots")
		) {
			setFilteredQuantifiedRegionOptions(_.filter(quantifiedRegionOptions, (o) => o.value !== "all"));
			setQuantifiedRegion(quantifiedRegionOptions[0].value);
		} else {
			setFilteredQuantifiedRegionOptions(quantifiedRegionOptions);
		}
	};

	// @ts-expect-error -- TS7006: implicit any (legacy, TODO: add explicit types)
	const filterDataContentOptions = (value) => {
		const match = fileFormatOptions.find((x) => x.value == value);
		if (match.type == ExportTypes.PlotGPSCoords) {
			setFilteredDataContentOptions(_.filter(dataContentOptions, (o) => o.value !== "all"));
			setDataContent(dataContentOptions[0].value);
		} else {
			setFilteredDataContentOptions(dataContentOptions);
		}
	};

	const generateStandardTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.fileName,
			accessToken,
		);
	};

	const generateStandardProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.fileName,
			accessToken,
		);
	};

	const generateStackedTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.fileName,
			accessToken,
		);
	};

	const generateTabbedTrialHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedTrialHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.fileName,
			accessToken,
		);
	};

	const generatePlotGPSTrialCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSTrialCoordsExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.fileName,
			accessToken,
		);
	};

	const generateStackedProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.isZipped,
			vars.fileName,
			accessToken,
		);
	};

	const generateTabbedProtocolHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedProtocolHeatmapExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.isForAdmin,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.fileName,
			accessToken,
		);
	};

	const generatePlotGPSProtocolCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSProtocolCoordsExcel(
			(dashboardOptions as any).trialIds,
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.fileName,
			accessToken,
		);
	};

	const generateStandardFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.fileName,
			accessToken,
		);
	};

	const generateStackedFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateStackedFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.fileName,
			accessToken,
		);
	};

	const generateTabbedFieldHeatmapExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generateTabbedFieldHeatmapExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.exclusions,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.isForCSV,
			vars.fileName,
			accessToken,
		);
	};

	const generatePlotGPSFieldCoordsExcel = async (vars: typeof dataDownload) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.generatePlotGPSFieldCoordsExcel(
			userAuth.currentClientId,
			(userAuth as any).currentClient.clientName,
			vars.id,
			vars.currentDateTimeString,
			vars.quantifiedRegion,
			vars.includeSubplots,
			vars.fileName,
			accessToken,
		);
	};

	const getProtocolDashboardOptions = async () => {
		const accessToken = await getTokenSilently();
		return protocolOptions.getProtocolDashboardOptions(
			moduleNavigation.protocolId ?? "",
			userAuth.currentClientId ?? "",
			accessToken,
		);
	};

	/**
	 * Compose the download name, reproducing what this modal produced before exports moved to blob
	 * storage. Sent without an extension: the backend appends the one matching what it generated, so
	 * a tabbed export can no longer be mislabelled `.csv` the way client-built names used to be.
	 *
	 * The `_Without_Exclusions` suffix stays scoped to Standard exports — Plot GPS also carries
	 * `exclusions: false`, but its names never had the suffix and should not gain one.
	 */
	const buildFileName = (exportType: ExportTypes, exclusions: boolean) => {
		const subject = localEntityName ?? type;
		const base = `${subject}_${FILE_NAME_LABELS[type][exportType]}_${moment().format("YYYYMMDD_hhmma")}`;
		return exportType === ExportTypes.Standard && !exclusions ? `${base}_Without_Exclusions` : base;
	};

	/** Poll a queued export. Fetches a fresh token per call, since exports can outlive one token. */
	const pollExportStatus = async (jobId: string, waitSeconds: number) => {
		const accessToken = await getTokenSilently();
		return excelGenerator.getExcelExportStatus(userAuth.currentClientId, jobId, waitSeconds, accessToken);
	};

	/**
	 * Queue an export, report each phase through a single sticky toast, then hand the finished job's
	 * SAS URL to the browser. Every export type shares this path: file naming, zipping and CSV-vs-xlsx
	 * are all decided by the backend now, so nothing here varies per endpoint.
	 */
	const runExportJob = async (request: ExportRequest) => {
		const { toastId } = request;
		toast.info("Starting export...", { toastId: toastId });

		try {
			const job = await runExcelExport(() => request.start(request.vars), pollExportStatus);

			if (job.status === "Ready" && job.downloadUrl) {
				toast.success(`Downloading ${job.fileName ?? "export"}`, { toastId: toastId });
				triggerExcelDownload(job.downloadUrl);
			} else if (job.status === "NoData") {
				// Not an error — none of the selected trials had any plot data.
				toast.info("There is no data to export for this selection.", { toastId: toastId });
			} else {
				toast.error(exportFailureMessage(job), { toastId: toastId });
			}

			return job;
		} catch (error) {
			toast.error(error instanceof Error ? error.message : "Data download failed.", { toastId: toastId });
			throw error;
		}
	};

	const getProtocolDashboardOptionsMutator = (options?: { onSuccess?: () => void; onError?: () => void }) =>
		useMutation({
			mutationFn: () => getProtocolDashboardOptions(),
			onSuccess: (data) => {
				options?.onSuccess?.();
				setDashboardOptions(data.data);
			},
			onError: (error) => {
				toast.error("Failed to get protocol dashboard options.");
				console.error(error);
				options?.onError?.();
			},
		});

	const { mutate: protocolDashboardOptionsMutate } = getProtocolDashboardOptionsMutator();

	const { mutate: exportMutate, isPending: isExporting } = useMutation({
		mutationFn: (request: ExportRequest) => runExportJob(request),
		onSuccess: (job, request) => {
			// A resolved job is not necessarily a delivered file — only reset the form once one
			// actually downloaded, matching what the old per-export success handlers did.
			if (request.resetAfter && job.status === "Ready") {
				resetInputs();
			}
		},
		onError: (error) => {
			// The user-facing message is already on the toast; this is for the console trail.
			console.error(error);
		},
	});

	/**
	 * Which endpoint each (entity type, file format) pair maps to. `resetAfter` mirrors the
	 * per-export reset behaviour that the individual mutators used to carry.
	 */
	const exportStarters: Record<
		Props["type"],
		Partial<Record<ExportTypes, { start: ExportRequest["start"]; resetAfter: boolean }>>
	> = {
		Trial: {
			[ExportTypes.Universal]: { start: generateStackedTrialHeatmapExcel, resetAfter: true },
			[ExportTypes.Standard]: { start: generateStandardTrialHeatmapExcel, resetAfter: false },
			[ExportTypes.Tabbed]: { start: generateTabbedTrialHeatmapExcel, resetAfter: true },
			[ExportTypes.PlotGPSCoords]: { start: generatePlotGPSTrialCoordsExcel, resetAfter: true },
		},
		Field: {
			[ExportTypes.Universal]: { start: generateStackedFieldHeatmapExcel, resetAfter: true },
			[ExportTypes.Standard]: { start: generateStandardFieldHeatmapExcel, resetAfter: false },
			[ExportTypes.Tabbed]: { start: generateTabbedFieldHeatmapExcel, resetAfter: true },
			[ExportTypes.PlotGPSCoords]: { start: generatePlotGPSFieldCoordsExcel, resetAfter: true },
		},
		Protocol: {
			[ExportTypes.Universal]: { start: generateStackedProtocolHeatmapExcel, resetAfter: false },
			[ExportTypes.Standard]: { start: generateStandardProtocolHeatmapExcel, resetAfter: false },
			[ExportTypes.Tabbed]: { start: generateTabbedProtocolHeatmapExcel, resetAfter: false },
			[ExportTypes.PlotGPSCoords]: { start: generatePlotGPSProtocolCoordsExcel, resetAfter: false },
		},
	};

	const exportData = () => {
		setOpen(false);
		const fileFormatMatch = fileFormatOptions.find((x) => x.value == fileFormat);
		const dataUnitMatch = dataUnitOptions.find((x) => x.value == dataUnit);
		const dataContentMatch = dataContentOptions.find((x) => x.value == dataContent);
		const quantifiedRegionMatch = quantifiedRegionOptions.find((x) => x.value == quantifiedRegion);

		// Snapshot the selections rather than passing the shared object, so a second export started
		// while this one is still queued cannot rewrite this one's parameters.
		const vars: typeof dataDownload = {
			id,
			includeSubplots: dataUnitMatch.includeSubplots,
			quantifiedRegion: quantifiedRegionMatch.value,
			isForAdmin: dataContentMatch.isForAdmin,
			exclusions: fileFormatMatch.exclusions,
			isForCSV: fileFormatMatch.isForCSV,
			isZipped: fileFormatMatch.isZipped,
			currentDateTimeString: JSON.stringify(new Date().toLocaleString()),
			fileName: buildFileName(fileFormatMatch.type as ExportTypes, fileFormatMatch.exclusions),
		};

		const selection = exportStarters[type]?.[fileFormatMatch.type as ExportTypes];
		if (!selection) {
			return false;
		}

		exportMutate({
			start: selection.start,
			vars,
			// Keyed so concurrent exports of different things each get their own progress toast.
			toastId: `excel-export-${type}-${id}-${fileFormat}`,
			resetAfter: selection.resetAfter,
		});
		return true;
	};

	return (
		<Modal open={open} style={{ width: "30%", maxHeight: "85%" }}>
			<Modal.Header>{`Download ${type} Data for ${localEntityName}`}</Modal.Header>
			<Modal.Content>
				<Form style={{ padding: "1em" }}>
					<div style={{ display: "flex", flexDirection: "column", paddingRight: "1em" }}>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="File Format"
								options={fileFormatOptions}
								value={fileFormat}
								onChange={(_e, { value }) => {
									setFileFormat(value as string);
									filterDataUnitOptions(value);
									filterQuantifiedRegionOptions(value);
									filterDataContentOptions(value);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Data Unit"
								options={filteredDataUnitOptions}
								value={dataUnit}
								onChange={(_e, { value }) => {
									setDataUnit(value as string);
									filterQuantifiedRegionOptions(value);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Quantified Region"
								options={filteredQuantifiedRegionOptions}
								value={quantifiedRegion}
								onChange={(_e, { value }) => {
									setQuantifiedRegion(value as string);
								}}
							/>
						</Form.Field>
						<Form.Field width={16} style={{ flexGrow: 1 }}>
							<Form.Select
								label="Data Content"
								options={filteredDataContentOptions}
								value={dataContent}
								onChange={(_e, { value }) => {
									setDataContent(value as string);
								}}
							/>
						</Form.Field>
					</div>
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Cancel"
					onClick={() => {
						setOpen(false);
						resetInputs();
					}}
				/>
				{/*
				 * The backend coalesces duplicate in-flight requests, so a repeat click cannot start
				 * redundant work — but blocking it here is still clearer than firing requests that
				 * silently resolve to the job already running.
				 */}
				<Button primary content="Export" onClick={exportData} disabled={isExporting} loading={isExporting} />
			</Modal.Actions>
		</Modal>
	);
};

export default HeatmapExportModal;
